import { createApi } from "@reduxjs/toolkit/query/react";

import ENDPOINTS from "../../lib/api/endpoints";
import { axiosBaseQuery } from "../../lib/api/rtkBaseQuery";
import { mapSupplier } from "../search/suppliersMappers";
import { FEED_SORT, MAX_DISTANCE_METERS } from "../search/supplierFilters";

export const SESSION_CACHE_SECONDS = 86400;
export const VOLUME_OFFERS_LIMIT = 200;

const locationParams = ({ latitude, longitude, distanceMeters }) => ({
  "location.latitude": latitude,
  "location.longitude": longitude,
  "location.distanceMeters": distanceMeters,
});

const toOfferRow = (row) => ({
  inStock: Boolean(row?.inStock),
  volume: row?.volume,
  supplier: { id: row?.supplier?.id },
  amount: { value: row?.amount?.value, currency: row?.amount?.currency },
});

export const homeApi = createApi({
  reducerPath: "homeApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Feed"],
  keepUnusedDataFor: SESSION_CACHE_SECONDS,
  endpoints: (builder) => ({
    getSupplierFeed: builder.query({
      query: (params) => ({ url: ENDPOINTS.SUPPLIERS.search, params }),
      transformResponse: (dto) => {
        const suppliers = (dto?.content || []).map(mapSupplier);

        return { suppliers, total: dto?.totalElements ?? suppliers.length };
      },
      providesTags: [{ type: "Feed", id: "SUPPLIERS" }],
    }),

    getNearestSupplierKm: builder.query({
      query: ({ latitude, longitude }) => ({
        url: ENDPOINTS.SUPPLIERS.search,
        params: {
          ...locationParams({ latitude, longitude, distanceMeters: MAX_DISTANCE_METERS }),
          sort: FEED_SORT,
          size: 1,
        },
      }),
      transformResponse: (dto) => {
        const meters = dto?.content?.[0]?.distanceToLocationMeters;

        return Number.isFinite(meters) ? meters / 1000 : null;
      },
      providesTags: [{ type: "Feed", id: "NEAREST" }],
    }),

    getCylindersSummary: builder.query({
      query: () => ({ url: ENDPOINTS.CATALOG.cylindersSummary }),
      transformResponse: (dto) => dto?.cylinders || [],
    }),

    getVolumeOffers: builder.query({
      query: ({ latitude, longitude, distanceMeters, containerOptionId }) => ({
        url: ENDPOINTS.CATALOG.productsSearch,
        params: {
          ...locationParams({ latitude, longitude, distanceMeters }),
          size: VOLUME_OFFERS_LIMIT,
          ...(containerOptionId ? { containerOptionId } : {}),
        },
      }),
      transformResponse: (dto) => {
        const rows = (dto?.content || []).map(toOfferRow);

        return { rows, truncated: rows.length >= VOLUME_OFFERS_LIMIT };
      },
      providesTags: [{ type: "Feed", id: "OFFERS" }],
    }),
  }),
});

export const {
  useGetSupplierFeedQuery,
  useGetNearestSupplierKmQuery,
  useGetCylindersSummaryQuery,
  useGetVolumeOffersQuery,
} = homeApi;
